'use client';

import { cn } from '~/lib/utils';
import Link from 'next/link';
import {
  type ComponentType,
  type KeyboardEventHandler,
  type MouseEventHandler,
  type ReactNode,
} from 'react';
import type { LucideIcon } from 'lucide-react';
import { usePathname } from 'next/navigation';
import Tooltip from '~/components/Tooltip';
import TruncatedText from '~/components/TruncatedText';
import {
  isPathOrDescendant,
  markPendingNavigation,
  usePendingHref,
} from '~/components/Navigation/routeNavigationStore';

type NavBarItemProps = {
  label: string;
  isActive?: boolean;
  icon: LucideIcon | ComponentType<{ className?: string }>;
  href?: string;
  newTab?: boolean;
  isSidebarExpanded?: boolean;
  onClick?: MouseEventHandler<HTMLAnchorElement | HTMLDivElement>;
  disableTooltip?: boolean;
  asButton?: boolean;
  badge?: ReactNode;
  badgeCount?: number;
};

function isPendingMatch(pendingHref: string, href: string): boolean {
  return isPathOrDescendant(pendingHref, href);
}

export default function NavBarItem(props: NavBarItemProps) {
  const Icon = props.icon;
  const expanded = props.isSidebarExpanded !== false;
  const pathname = usePathname();
  const pendingHref = usePendingHref();

  const pendingActive = !!props.href && !!pendingHref && isPendingMatch(pendingHref, props.href);
  const isActive = pendingHref ? pendingActive : !!props.isActive;

  const accessibleLabel =
    props.badgeCount != null && props.badgeCount > 0
      ? `${props.label}, ${props.badgeCount} unread`
      : props.label;

  const commonClassName = cn(
    'flex items-center gap-2 rounded-md py-2 text-sm transition-colors no-underline outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
    // Collapsed: fill the rail column so wrappers (tooltip/popout) cannot shift the 16px icon.
    expanded ? 'w-full justify-start px-2' : 'h-9 w-full justify-center px-0',
    isActive
      ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
      : 'text-muted-foreground hover:bg-sidebar-accent/60 hover:text-foreground',
  );

  const content = (
    <>
      <Icon className="block size-4 shrink-0" />
      {expanded ? (
        <TruncatedText className="min-w-0 flex-1 whitespace-nowrap">{props.label}</TruncatedText>
      ) : null}
      {expanded && props.badge ? <span className="shrink-0">{props.badge}</span> : null}
    </>
  );

  const handleClick: MouseEventHandler<HTMLAnchorElement | HTMLDivElement> = (e) => {
    props.onClick?.(e);
    // Modified / non-primary clicks don't navigate this tab — don't strand pending.
    if (
      e.defaultPrevented ||
      props.newTab ||
      props.asButton ||
      !props.href ||
      e.button !== 0 ||
      e.metaKey ||
      e.ctrlKey ||
      e.shiftKey ||
      e.altKey
    ) {
      return;
    }
    markPendingNavigation(props.href, pathname);
  };

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    e.currentTarget.click();
  };

  const linkContent = props.asButton ? (
    <div
      className={cn(commonClassName, 'cursor-pointer')}
      data-testid="nav-bar-item"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={accessibleLabel}
    >
      {content}
    </div>
  ) : (
    <Link
      href={{ pathname: props.href, query: '' }}
      target={props.newTab ? '_blank' : undefined}
      className={commonClassName}
      data-testid="nav-bar-item"
      onClick={handleClick}
      aria-label={!expanded ? accessibleLabel : undefined}
    >
      {content}
    </Link>
  );

  if (props.disableTooltip || expanded) {
    return linkContent;
  }

  return (
    <Tooltip
      content={props.label}
      hideIcon
      delay={300}
      isNotInteractive
      anchorClassName="flex w-full justify-center"
      popoverProps={{
        anchorOrigin: {
          vertical: 'center',
          horizontal: 'right',
        },
        transformOrigin: {
          vertical: 'center',
          horizontal: 'left',
        },
        slotProps: {
          paper: {
            sx: { marginLeft: '8px' },
          },
        },
      }}
    >
      {linkContent}
    </Tooltip>
  );
}
